import { useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import L, { type LatLngExpression } from "leaflet";
import "leaflet/dist/leaflet.css";

import { getFlightDetail, getFlightReplay2d, type ReplayPoint } from "@/api/flightDetail";

interface TrainingPopupProps {
  flightId: number;
  focusText: string;
  open: boolean;
  onClose: () => void;
}

interface BasePoint {
  latitude: number;
  longitude: number;
}

interface Preset {
  label: string;
  description: string;
  routeOffsets: [number, number][];
  ringOffsets: [number, number][];
}

function resolvePreset(focusText: string): Preset {
  const normalized = (focusText || "").toLowerCase();

  if (/(turn|heading|bank|yaw|corner)/.test(normalized)) {
    return {
      label: "turn timing",
      description:
        "The drill chains several offset gates together so the pilot has to slow down, square the turn, and then exit cleanly into the next leg.",
      routeOffsets: [
        [0, 0],
        [110, 40],
        [180, 180],
        [70, 300],
        [-60, 210],
        [-150, 60],
        [-220, -90],
        [-70, -200],
        [90, -120],
        [170, 30],
        [40, 150],
      ],
      ringOffsets: [
        [180, 180],
        [-60, 210],
        [-70, -200],
      ],
    };
  }

  if (/(speed|pace|throttle|accel|decel|fast|slow|roll|aggressive)/.test(normalized)) {
    return {
      label: "speed control",
      description:
        "The drill alternates longer acceleration legs with tighter gates so the pilot has to bleed speed before each change of direction.",
      routeOffsets: [
        [0, 0],
        [10, 180],
        [70, 380],
        [190, 500],
        [280, 320],
        [250, 80],
        [180, -140],
        [40, -250],
        [-110, -120],
        [-120, 110],
        [-10, 250],
      ],
      ringOffsets: [
        [70, 380],
        [250, 80],
        [-120, 110],
      ],
    };
  }

  if (/(approach|line|path|checkpoint|corridor|entry|exit)/.test(normalized)) {
    return {
      label: "line discipline",
      description:
        "The drill keeps the route inside a corridor-style pattern so the pilot has to enter each checkpoint on a cleaner line instead of cutting across the arc.",
      routeOffsets: [
        [0, 0],
        [90, 80],
        [190, 140],
        [270, 50],
        [220, -100],
        [120, -180],
        [0, -210],
        [-120, -120],
        [-160, 20],
        [-70, 140],
        [30, 190],
      ],
      ringOffsets: [
        [190, 140],
        [120, -180],
        [-70, 140],
      ],
    };
  }

  return {
    label: "smoothness",
    description:
      "The drill mixes wide arcs, staggered gates, and quick resets to reinforce steadier stick inputs across the whole route.",
    routeOffsets: [
      [0, 0],
      [120, 90],
      [190, 220],
      [80, 330],
      [-40, 260],
      [-140, 120],
      [-180, -20],
      [-100, -160],
      [40, -220],
      [170, -110],
      [210, 50],
      [90, 150],
    ],
    ringOffsets: [
      [190, 220],
      [-140, 120],
      [40, -220],
    ],
  };
}

function offsetLatLng(
  base: BasePoint,
  northMeters: number,
  eastMeters: number,
): [number, number] {
  const metersPerDegreeLat = 111320;
  const latitudeOffset = northMeters / metersPerDegreeLat;
  const longitudeScale = Math.max(
    Math.cos((base.latitude * Math.PI) / 180) * metersPerDegreeLat,
    1,
  );
  const longitudeOffset = eastMeters / longitudeScale;
  return [base.latitude + latitudeOffset, base.longitude + longitudeOffset];
}

function pickBasePoint(
  homePoint: BasePoint | null,
  replayPoints: ReplayPoint[] | undefined,
): BasePoint | null {
  if (homePoint && homePoint.latitude != null && homePoint.longitude != null) {
    return { latitude: homePoint.latitude, longitude: homePoint.longitude };
  }
  const filtered = (replayPoints ?? []).filter(
    (p) => p.latitude != null && p.longitude != null,
  );
  if (!filtered.length) return null;
  const mid = filtered[Math.floor(filtered.length / 2)];
  return { latitude: mid.latitude, longitude: mid.longitude };
}

export function TrainingPopup({ flightId, focusText, open, onClose }: TrainingPopupProps) {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);

  const flightQ = useQuery({
    queryKey: ["flight", flightId, "detail-for-training"],
    queryFn: () => getFlightDetail(flightId),
    enabled: open,
  });
  const replayQ = useQuery({
    queryKey: ["flight", flightId, "replay-2d"],
    queryFn: () => getFlightReplay2d(flightId),
    enabled: open,
  });

  const homePoint =
    ((flightQ.data as unknown as {
      home_point?: { latitude: number; longitude: number; label?: string } | null;
    } | undefined)?.home_point) ?? null;

  const preset = useMemo(() => resolvePreset(focusText), [focusText]);

  const scenario = useMemo(() => {
    const base = pickBasePoint(homePoint, replayQ.data?.points);
    if (!base) return null;
    const points = preset.routeOffsets.map(([n, e]) => offsetLatLng(base, n, e));
    const rings = preset.ringOffsets.map(([n, e], idx) => ({
      latlng: offsetLatLng(base, n, e),
      radius: 52 + idx * 8,
    }));
    return { base, points, rings };
  }, [homePoint, replayQ.data?.points, preset]);

  const description = useMemo(() => {
    if (focusText) {
      const trimmed = focusText.trim().replace(/\.$/, "");
      return `Mock route for: ${trimmed}.`;
    }
    return `Mock ${preset.label} route with replay-style gates.`;
  }, [focusText, preset.label]);

  // Lock scroll while open
  useEffect(() => {
    if (!open) return;
    document.body.classList.add("training-dialog-open");
    return () => {
      document.body.classList.remove("training-dialog-open");
    };
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Create / destroy Leaflet map when dialog opens
  useEffect(() => {
    if (!open) return;
    if (!mapContainerRef.current) return;
    if (mapRef.current) return;

    const map = L.map(mapContainerRef.current, {
      center: [0, 0],
      zoom: 2,
      worldCopyJump: true,
    });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap contributors",
      maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;

    const raf = window.setTimeout(() => map.invalidateSize(), 60);
    return () => {
      window.clearTimeout(raf);
      map.remove();
      mapRef.current = null;
    };
  }, [open]);

  // Draw scenario layers
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !scenario) return;
    const layers: L.Layer[] = [];

    const routeLine = L.polyline(scenario.points as LatLngExpression[], {
      color: "#F0B429",
      weight: 4,
      opacity: 0.85,
    }).addTo(map);
    layers.push(routeLine);

    const start = scenario.points[0];
    const end = scenario.points[scenario.points.length - 1];
    if (start) {
      const marker = L.circleMarker(start as LatLngExpression, {
        radius: 7,
        color: "#38BDF8",
        fillColor: "#38BDF8",
        fillOpacity: 1,
        weight: 2,
      }).addTo(map);
      layers.push(marker);
    }
    if (end) {
      const marker = L.circleMarker(end as LatLngExpression, {
        radius: 7,
        color: "#38BDF8",
        fillColor: "#38BDF8",
        fillOpacity: 1,
        weight: 2,
      }).addTo(map);
      layers.push(marker);
    }

    scenario.rings.forEach((ring) => {
      const circle = L.circle(ring.latlng as LatLngExpression, {
        radius: ring.radius,
        color: "#38BDF8",
        weight: 2,
        dashArray: "5 6",
        fillColor: "#38BDF8",
        fillOpacity: 0.08,
      }).addTo(map);
      layers.push(circle);
    });

    const bounds = L.latLngBounds(scenario.points as LatLngExpression[]);
    scenario.rings.forEach((r) => bounds.extend(r.latlng as LatLngExpression));
    map.fitBounds(bounds, { padding: [28, 28] });

    return () => {
      layers.forEach((layer) => map.removeLayer(layer));
    };
  }, [scenario]);

  // Keep map sized correctly when window resizes while open
  useEffect(() => {
    if (!open) return;
    const handle = () => mapRef.current?.invalidateSize();
    window.addEventListener("resize", handle);
    return () => window.removeEventListener("resize", handle);
  }, [open]);

  if (!open) return null;

  const loading = flightQ.isLoading || replayQ.isLoading;

  return (
    <>
      <div
        className="training-dialog-backdrop"
        onClick={onClose}
        aria-hidden="true"
      />
      <section
        className="training-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="training-dialog-title"
      >
        <div className="training-dialog-panel">
          <header className="training-dialog-head">
            <div className="training-dialog-heading">
              <div className="training-dialog-title-row">
                <h2 id="training-dialog-title">Personalized training</h2>
                <span className="training-dialog-badge">MOCKUP</span>
              </div>
              <p className="training-dialog-copy">{description}</p>
            </div>
            <button
              className="button ghost training-dialog-close"
              type="button"
              onClick={onClose}
            >
              Close
            </button>
          </header>

          <div className="training-map-surface">
            {!scenario && !loading && (
              <p className="training-dialog-empty">
                No positional data is available to mock a training route for this flight.
              </p>
            )}
            <div
              ref={mapContainerRef}
              className="training-map-canvas"
              aria-hidden={!scenario}
            />
          </div>

          <footer className="training-dialog-actions">
            <span className="training-dialog-hint">
              Mocked path only. Use it as a drill prompt, not as a replay export.
            </span>
            <button className="button mockup" type="button" disabled={!scenario}>
              Play in VBS4
            </button>
          </footer>
        </div>
      </section>
    </>
  );
}
